import React, { useState } from 'react';
import SEO from '../../components/ui/SEO';
import CookiePolicySection from '../../components/ui/CookiePolicySection';
import { ChevronDown, ChevronUp, Shield, Lock, FileText, AlertCircle, Eye, Users, Mail } from 'lucide-react';
export default function Privacy() {
  const [openSections, setOpenSections] = useState({
    introduction: true,
    dataCollection: true,
    dataSharing: true,
    cookies: true,
    security: true,
    international: true,
    rights: true,
    complaints: true,
    changes: true,
    contact: true,
    schedule: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-749" data-mp="3qp42dk7i4ral">
      <SEO title="Privacy and Cookie Policy | Medicolegal Psychiatry" description="How Medicolegal Psychiatry collects, processes, stores and protects personal information under UK GDPR. Contact us with any data protection query." path="/policies/privacy" keywords={['privacy policy', 'cookie policy', 'expert witness data protection']} imageAlt="Privacy and cookie policy for Medicolegal Psychiatry" data-id="element-750" data-mp="1jy4juklsn157" />
      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-751" data-mp="11evmba7hr4q7">
        <div className="container mx-auto px-4" data-id="element-752" data-mp="11hrlt2yzm1gr">
          <div className="flex items-center justify-center mb-4" data-id="element-753" data-mp="dqvxwgxnjqes">
            <Shield className="h-12 w-12 mr-4" data-id="element-754" data-mp="8qzqs8tnriv0" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-755" data-mp="8sauy9r0msmn">
              Privacy and Cookie Policy
            </h1>
          </div>
          <p className="text-lg max-w-3xl mx-auto text-center" data-id="element-756" data-mp="ioe80aneobod">
            Last updated: 1 October 2026
          </p>
        </div>
      </section>
      {/* Policy Content */}
      <section className="py-12" data-id="element-757" data-mp="1u42u6zsu56o4">
        <div className="container mx-auto px-4" data-id="element-758" data-mp="1u4udpc95rdy1">
          <div className="max-w-3xl mx-auto" data-id="element-759" data-mp="1u65hhc2lslp5">
            <p className="text-slate-dark text-lg mb-8" data-id="element-760" data-mp="qtydz4cmc5uo">
              This Privacy and Cookie Policy explains how Medicolegal Psychiatry
              (referred to as "we," "us," or "our"), operated by Dr. Imtiaz
              Syed, collects, uses, processes, and protects your personal
              information when you use our services, visit our website
              (medicolegal-psychiatry.co.uk), or otherwise interact with us.
            </p>
            <div className="space-y-4 mb-8" data-id="element-761" data-mp="80kupwi9pnuw">
              {/* Introduction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-762" data-mp="d20gjqs9ryec">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.introduction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('introduction')} data-id="element-763" data-mp="h1sqa6tegala">
                  <div className="flex items-center" data-id="element-764" data-mp="3pi41hduqvs85">
                    <FileText className="h-5 w-5 mr-2" data-id="element-765" data-mp="3hznz8mp40l1r" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-766" data-mp="3hz4gx4vjrun7">
                      1. Introduction
                    </h2>
                  </div>
                  {openSections.introduction ? <ChevronUp className="h-5 w-5" data-id="element-767" data-mp="38xeru5p517sl" /> : <ChevronDown className="h-5 w-5" data-id="element-768" data-mp="33ytpo0qbse1i" />}
                </button>
                {openSections.introduction && <div className="p-6 bg-white" data-id="element-769" data-mp="2z2mtgznqegwr">
                    <p className="text-slate-dark mb-4" data-id="element-770" data-mp="2u90pdvvmvlck">
                      Welcome to Medicolegal Psychiatry. We are committed to
                      upholding the highest standards of privacy and data
                      protection in all our operations. This policy outlines our
                      practices for handling personal data, ensuring compliance
                      with the UK General Data Protection Regulation (UK GDPR),
                      the Data Protection Act 2018 (DPA 2018), and other
                      relevant legislation.
                    </p>
                  </div>}
              </div>
              {/* Data Collection Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-771" data-mp="5j1enmng0bqn">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataCollection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataCollection')} data-id="element-772" data-mp="agt7qjztq3wf">
                  <div className="flex items-center" data-id="element-773" data-mp="3ip1k62g779qh">
                    <Eye className="h-5 w-5 mr-2" data-id="element-774" data-mp="3iqcoc3dk2ji4" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-775" data-mp="3nnmp2f6eahl9">
                      2. How We Collect and Use Your Information
                    </h2>
                  </div>
                  {openSections.dataCollection ? <ChevronUp className="h-5 w-5" data-id="element-776" data-mp="1lv6t9i73u79a" /> : <ChevronDown className="h-5 w-5" data-id="element-777" data-mp="36dr2eydna4go" />}
                </button>
                {openSections.dataCollection && <div className="p-6 bg-white" data-id="element-778" data-mp="31dcstbweamgj">
                    <p className="text-slate-dark mb-4" data-id="element-779" data-mp="3g4loo2qw5424">
                      We collect and process personal data primarily to provide
                      our medico-legal psychiatric assessments and expert
                      witness services. This includes:
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-780" data-mp="3k6got3x35beb">
                      a) Personal Information We Collect:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-781" data-mp="2vr2m6i8t5e5">
                      <li className="mb-2" data-id="element-782" data-mp="3u5fymptl7d2w">
                        Contact Details: Your name, title, professional address,
                        email address, and phone number.
                      </li>
                      <li className="mb-2" data-id="element-783" data-mp="1zrojxbwei30l">
                        Client Information: If you are an instructing party, we
                        will process personal data relating to your client,
                        including their name, contact details, date of birth,
                        photographic identification, and special category data
                        such as medical records, mental health history, and case
                        documentation.
                      </li>
                      <li className="mb-2" data-id="element-784" data-mp="38cv4fu83fjib">
                        Account Information: For our clients, this may include
                        usernames, passwords, security questions, and
                        preferences.
                      </li>
                      <li className="mb-2" data-id="element-785" data-mp="3m51npp6jm6lz">
                        Financial Information: Payment details, transaction
                        history, and billing information.
                      </li>
                      <li className="mb-2" data-id="element-786" data-mp="3w1wji0bflswk">
                        Communications: Records of your interactions with us via
                        email, phone, or post.
                      </li>
                      <li className="mb-2" data-id="element-787" data-mp="1ffzv5i9xcbf5">
                        Website Usage Data: Where you consent, information
                        collected through cookies and similar technologies when
                        you visit our website, such as pages viewed, device and
                        browser type, approximate location, and how you scroll
                        and click. See section 4.
                      </li>
                      <li data-id="element-788" data-mp="1y7iyjq7v1rhp">
                        Professional Details: Information about your
                        professional standing, such as SRA registration status
                        and professional memberships.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-789" data-mp="2lx3j2djnwwt3">
                      b) How We Use Your Information:
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-790" data-mp="1pi20iemtbbdm">
                      <li className="mb-2" data-id="element-791" data-mp="1pgqs21if9fg6">
                        Service Provision: To conduct assessments, prepare
                        medico-legal reports, provide expert witness testimony,
                        and manage appointments.
                      </li>
                      <li className="mb-2" data-id="element-792" data-mp="1lh8czmda7yl9">
                        Contractual Obligations: To manage our relationship with
                        you, process invoices, and receive payments.
                      </li>
                      <li className="mb-2" data-id="element-793" data-mp="2d6tiyealofd7">
                        Legal & Regulatory Compliance: To comply with legal
                        obligations, professional requirements, and respond to
                        lawful requests from courts, tribunals, or regulatory
                        bodies.
                      </li>
                      <li className="mb-2" data-id="element-794" data-mp="srhw55k9milg">
                        Communication: To respond to your enquiries, provide
                        updates on your case, and send important information.
                      </li>
                      <li className="mb-2" data-id="element-795" data-mp="g842rcbn771f">
                        Business Improvement: To analyse website usage, improve
                        our services, and maintain security.
                      </li>
                      <li data-id="element-796" data-mp="pb57b8kv0q2z">
                        Risk Management: To ensure the safety of our staff
                        during visits.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-797" data-mp="3tp3d5sbdebtl">
                      c) Legal Grounds for Processing:
                    </h3>
                    <p className="text-slate-dark mb-2" data-id="element-798" data-mp="6jcfvilfvcxd">
                      We process your personal data based on the following
                      lawful bases:
                    </p>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-799" data-mp="3ewt68wzuz7qa">
                      <li className="mb-2" data-id="element-800" data-mp="39zjhljkan03i">
                        Performance of a Contract: Necessary for providing our
                        medico-legal services as per your instruction.
                      </li>
                      <li className="mb-2" data-id="element-801" data-mp="2f59uks5vqq8w">
                        Compliance with a Legal Obligation: To meet our duties
                        as a healthcare professional and expert witness, and for
                        regulatory compliance.
                      </li>
                      <li className="mb-2" data-id="element-802" data-mp="21964nb21h428">
                        Legitimate Interests: To manage our business operations,
                        improve services, ensure security, and for effective
                        communication.
                      </li>
                      <li className="mb-2" data-id="element-803" data-mp="1mjgs6johxdmn">
                        Consent: For processing special category data where
                        explicit consent is obtained, for specific marketing
                        communications, and for analytics and marketing cookies
                        on our website. You can withdraw cookie consent at any
                        time using the Cookie preferences link in the website
                        footer.
                      </li>
                      <li data-id="element-804" data-mp="204ta9b1icww">
                        Vital Interests: In rare cases, to protect the vital
                        interests of an individual.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Data Sharing Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-805" data-mp="3afoimx8lbb6o">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataSharing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataSharing')} data-id="element-806" data-mp="3ff187pquiwcy">
                  <div className="flex items-center" data-id="element-807" data-mp="2rpyqyj04j5iz">
                    <Users className="h-5 w-5 mr-2" data-id="element-808" data-mp="2rodxkwpjpsmp" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-809" data-mp="3v0mfd3g6vgd9">
                      3. Data Sharing
                    </h2>
                  </div>
                  {openSections.dataSharing ? <ChevronUp className="h-5 w-5" data-id="element-810" data-mp="7njyj5ektqsc" /> : <ChevronDown className="h-5 w-5" data-id="element-811" data-mp="7pmojpfqbjx2" />}
                </button>
                {openSections.dataSharing && <div className="p-6 bg-white" data-id="element-812" data-mp="3c31ltj2hbtmb">
                    <p className="text-slate-dark mb-4" data-id="element-813" data-mp="3c3uwuw8h15a9">
                      We may share your personal information in the following
                      circumstances:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-814" data-mp="3l0bzfb2rruue">
                      <li className="mb-2" data-id="element-815" data-mp="3g225f3ss70ad">
                        With You: As the Data Controller, we will share
                        information with you as required by our contract and
                        your instructions.
                      </li>
                      <li className="mb-2" data-id="element-816" data-mp="13u58n33tejtm">
                        With Your Clients: If you are the instructing party, we
                        will share relevant information with your client as
                        necessary for the assessment and reporting process.
                      </li>
                      <li className="mb-2" data-id="element-817" data-mp="yuila0u9vn73">
                        With Other Experts: If required for a joint report or to
                        obtain further information relevant to the case.
                      </li>
                      <li className="mb-2" data-id="element-818" data-mp="1rjg17xb0h7wo">
                        With Courts, Tribunals, and Regulatory Bodies: As
                        legally mandated.
                      </li>
                      <li className="mb-2" data-id="element-819" data-mp="1ht4nxk5ftmd4">
                        With Third-Party Service Providers: For IT support,
                        secure cloud storage, and payment processing, all bound
                        by strict confidentiality agreements.
                      </li>
                      <li className="mb-2" data-id="element-820" data-mp="lowcgj6w4yhx">
                        With Website Analytics and Advertising Providers: Only
                        where you have consented, website usage data is shared
                        with Google (Google Analytics), Microsoft (Microsoft
                        Clarity) and LinkedIn (LinkedIn Insight Tag). These
                        providers do not receive the content of any instruction,
                        report or case file.
                      </li>
                      <li className="mb-2" data-id="element-821" data-mp="10ellt1p508ta">
                        For Safety: If necessary, to ensure the safety of our
                        staff during site visits, we may arrange for an
                        accompanying person.
                      </li>
                      <li data-id="element-822" data-mp="1o2v1dlvrc0bz">
                        With Legal or Regulatory Authorities: If required by law
                        or in response to valid legal process.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-823" data-mp="1qocfcb4etus3">
                      We do not sell your personal information.
                    </p>
                  </div>}
              </div>
              {/* Cookies Section */}
              <div id="cookies" className="border border-gray-200 rounded-lg overflow-hidden scroll-mt-24" data-id="element-824" data-mp="20kfjtnmqi5i7">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cookies ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cookies')} data-id="element-825" data-mp="29n6ydwawphe2">
                  <div className="flex items-center" data-id="element-826" data-mp="2obbgay24xovs">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-827" data-mp="2oas0bmw1pake" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-828" data-mp="2taxupatp9b66">
                      4. Cookies and Website Usage
                    </h2>
                  </div>
                  {openSections.cookies ? <ChevronUp className="h-5 w-5" data-id="element-829" data-mp="y6gekmqyz4u2" /> : <ChevronDown className="h-5 w-5" data-id="element-830" data-mp="t8evi1tjn5il" />}
                </button>
                {openSections.cookies && <CookiePolicySection data-id="element-831" data-mp="16ztgge70hgsa" />}
              </div>
              {/* Security Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-832" data-mp="128l0t0f38z6o">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.security ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('security')} data-id="element-833" data-mp="1gx7te3tq7nfu">
                  <div className="flex items-center" data-id="element-834" data-mp="1pw2zlfwrsxj5">
                    <Lock className="h-5 w-5 mr-2" data-id="element-835" data-mp="1ly5ase6wwdga" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-836" data-mp="3rpskickxr5pm">
                      5. Data Security and Retention
                    </h2>
                  </div>
                  {openSections.security ? <ChevronUp className="h-5 w-5" data-id="element-837" data-mp="3djqjzbook612" /> : <ChevronDown className="h-5 w-5" data-id="element-838" data-mp="3hlu93349v94t" />}
                </button>
                {openSections.security && <div className="p-6 bg-white" data-id="element-839" data-mp="33te3bujzkx07">
                    <p className="text-slate-dark mb-4" data-id="element-840" data-mp="33smer3ot3zjf">
                      We implement robust technical and organisational measures
                      to protect your personal data against unauthorised access,
                      loss, alteration, or disclosure. This includes secure data
                      storage, access controls, and regular security
                      assessments.
                    </p>
                    <p className="text-slate-dark" data-id="element-841" data-mp="2pu60mrom76r9">
                      We retain personal data only for as long as necessary to
                      fulfil the purposes outlined in this policy, comply with
                      legal and regulatory obligations, resolve disputes, and
                      enforce our agreements. Retention periods are based on
                      legal requirements (e.g., medical record retention) and
                      operational needs.
                    </p>
                  </div>}
              </div>
              {/* Additional sections follow the same pattern */}
              {/* International Data Transfers */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-842" data-mp="1dx8t9o1gu8ly">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.international ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('international')} data-id="element-843" data-mp="1duw68wgup821">
                  <div className="flex items-center" data-id="element-844" data-mp="zzamp4tdzgh0">
                    <FileText className="h-5 w-5 mr-2" data-id="element-845" data-mp="2yddn7yuo8wwd" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-846" data-mp="2yarfnz7s6he5">
                      6. International Data Transfers
                    </h2>
                  </div>
                  {openSections.international ? <ChevronUp className="h-5 w-5" data-id="element-847" data-mp="l2kl77ltrlgq" /> : <ChevronDown className="h-5 w-5" data-id="element-848" data-mp="l0hipo3hjzv4" />}
                </button>
                {openSections.international && <div className="p-6 bg-white" data-id="element-849" data-mp="tu2xh7fnhh4j">
                    <p className="text-slate-dark mb-4" data-id="element-850" data-mp="twyxr2ez0hw5">
                      Your personal information will primarily be processed
                      within the UK. If data is transferred outside the UK or
                      European Economic Area (EEA), we will ensure that
                      appropriate safeguards are in place, such as Standard
                      Contractual Clauses, to protect your data in accordance
                      with applicable laws.
                    </p>
                    <p className="text-slate-dark" data-id="element-851" data-mp="1hn32jd3wanoq">
                      Where you consent to analytics or marketing cookies,
                      website usage data is processed by Google, Microsoft and
                      LinkedIn, which may transfer it to the United States.
                      These transfers rely on the UK Extension to the EU-US
                      Data Privacy Framework or the UK International Data
                      Transfer Addendum, as applicable to each provider.
                    </p>
                  </div>}
              </div>
              {/* Your Rights */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-852" data-mp="1a0876y2azesj">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.rights ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('rights')} data-id="element-853" data-mp="1ezulzbxs533g">
                  <div className="flex items-center" data-id="element-854" data-mp="2qeq79gqxm2sz">
                    <Shield className="h-5 w-5 mr-2" data-id="element-855" data-mp="2vdba7nxkj0l4" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-856" data-mp="2vfxijpsa9k4e">
                      7. Your Rights
                    </h2>
                  </div>
                  {openSections.rights ? <ChevronUp className="h-5 w-5" data-id="element-857" data-mp="etr3tj1tjtgr" /> : <ChevronDown className="h-5 w-5" data-id="element-858" data-mp="9zd79dbf7gai" />}
                </button>
                {openSections.rights && <div className="p-6 bg-white" data-id="element-859" data-mp="2rx6dkjnezt">
                    <p className="text-slate-dark mb-4" data-id="element-860" data-mp="3jo3b374ojr">
                      As data subjects, you have several rights regarding your
                      personal data:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-861" data-mp="3n897z9on2ov3">
                      <li className="mb-2" data-id="element-862" data-mp="3i96dgj6ac11d">
                        Right to Access: Request a copy of the personal data we
                        hold about you.
                      </li>
                      <li className="mb-2" data-id="element-863" data-mp="1ggi7i229azhw">
                        Right to Rectification: Request correction of inaccurate
                        or incomplete personal data.
                      </li>
                      <li className="mb-2" data-id="element-864" data-mp="3fo6yuzfld0xe">
                        Right to Erasure (Right to be Forgotten): Request
                        deletion of your personal data under certain conditions.
                      </li>
                      <li className="mb-2" data-id="element-865" data-mp="35yx7xmwhhoav">
                        Right to Restriction of Processing: Request that we
                        limit the way we use your personal data.
                      </li>
                      <li className="mb-2" data-id="element-866" data-mp="2gnfezonk3yy">
                        Right to Data Portability: Request a transfer of your
                        personal data to you or another controller in a
                        structured, commonly used, and machine-readable format.
                      </li>
                      <li className="mb-2" data-id="element-867" data-mp="28927363covg3">
                        Right to Object: Object to processing based on
                        legitimate interests or for direct marketing purposes.
                      </li>
                      <li className="mb-2" data-id="element-868" data-mp="vdo9c4d69lmi">
                        Rights Related to Automated Decision-Making and
                        Profiling: You have the right not to be subject to a
                        decision based solely on automated processing.
                      </li>
                      <li data-id="element-869" data-mp="108tpemjw862o">
                        Right to Withdraw Consent: If we rely on your consent
                        for processing, you have the right to withdraw it at any
                        time.
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-870" data-mp="legtnaiytew8">
                      Exercising Your Rights: To exercise any of your rights,
                      please contact us in writing using the details provided
                      below. We may ask for proof of identity to verify your
                      request. We aim to respond to all legitimate requests
                      within one month. If your request is complex or numerous,
                      we may extend this period by two months.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-871" data-mp="bhvbok4boawq">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-872" data-mp="gaociwwo6z3j">
                  <div className="flex items-center" data-id="element-873" data-mp="u9e8uabo0uqu">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-874" data-mp="u7uwgut4r3uc" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-875" data-mp="z7fwz9ny7k96">
                      8. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-876" data-mp="310x0c151pyy4" /> : <ChevronDown className="h-5 w-5" data-id="element-877" data-mp="2tguq1nk9i4yu" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-878" data-mp="2ont9lb4epxd5">
                    <p className="text-slate-dark mb-4" data-id="element-879" data-mp="2jq05cnmnzdfi">
                      If you are unhappy with how we have handled your personal
                      data or any aspect of our privacy practices, you have the
                      right to lodge a complaint with the Information
                      Commissioner's Office (ICO), the UK's supervisory
                      authority for data protection issues.
                    </p>
                    <p className="text-slate-dark mb-2" data-id="element-880" data-mp="32fg8lipqhjzm">
                      ICO Website: www.ico.org.uk
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-881" data-mp="1r247hflczfh5">
                      ICO Helpline: 0303 123 1113
                    </p>
                    <p className="text-slate-dark" data-id="element-882" data-mp="1m1zs5sw6sqsi">
                      We would appreciate the opportunity to address your
                      concerns directly before you approach the ICO. Please
                      contact us first.
                    </p>
                  </div>}
              </div>
              {/* Changes to Policy */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-883" data-mp="94ir55fuhrc9">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.changes ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('changes')} data-id="element-884" data-mp="96c0ishuepny">
                  <div className="flex items-center" data-id="element-885" data-mp="3hlvrfkv1jvzu">
                    <FileText className="h-5 w-5 mr-2" data-id="element-886" data-mp="3mivwbsg5mspd" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-887" data-mp="3mjnjqfzllk4k">
                      9. Changes to This Privacy and Cookie Policy
                    </h2>
                  </div>
                  {openSections.changes ? <ChevronUp className="h-5 w-5" data-id="element-888" data-mp="2szfyzlrvnbvh" /> : <ChevronDown className="h-5 w-5" data-id="element-889" data-mp="2t1ilvwxqgovz" />}
                </button>
                {openSections.changes && <div className="p-6 bg-white" data-id="element-890" data-mp="32y574mtz1hyj">
                    <p className="text-slate-dark" data-id="element-891" data-mp="32ywy1kcmirih">
                      We may update this Privacy and Cookie Policy from time to
                      time to reflect changes in our services, practices, or
                      legal requirements. Any changes will be posted on our
                      website with an updated "Last Updated" date. We encourage
                      you to review this policy regularly.
                    </p>
                  </div>}
              </div>
              {/* Contact Us */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-892" data-mp="3vlriwidcrswh">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.contact ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('contact')} data-id="element-893" data-mp="3qn6j2k20jb8k">
                  <div className="flex items-center" data-id="element-894" data-mp="2g8424dhvn048">
                    <Mail className="h-5 w-5 mr-2" data-id="element-895" data-mp="2l8s23oo32ral" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-896" data-mp="2l6yv289k6522">
                      10. Contact Us
                    </h2>
                  </div>
                  {openSections.contact ? <ChevronUp className="h-5 w-5" data-id="element-897" data-mp="1si9pnx1gzdki" /> : <ChevronDown className="h-5 w-5" data-id="element-898" data-mp="1xjyz5891nj6h" />}
                </button>
                {openSections.contact && <div className="p-6 bg-white" data-id="element-899" data-mp="279a9szbkwvwh">
                    <p className="text-slate-dark mb-4" data-id="element-900" data-mp="27a20pwu8e5gf">
                      For any questions about this Privacy and Cookie Policy,
                      our data protection practices, or to exercise your rights,
                      please contact us:
                    </p>
                    <p className="text-slate-dark" data-id="element-901" data-mp="3s27o9ril703y">
                      Email: enquiries@medicolegal-psychiatry.co.uk
                    </p>
                  </div>}
              </div>
              {/* Schedule */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-902" data-mp="3j0q1jqaoc06a">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.schedule ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('schedule')} data-id="element-903" data-mp="3e2y8gsyam80i">
                  <div className="flex items-center" data-id="element-904" data-mp="30b9zuzn961oa">
                    <FileText className="h-5 w-5 mr-2" data-id="element-905" data-mp="309p1t01qc0lo" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-906" data-mp="2ve9r2gz0qkth">
                      Schedule 1: Processing, Personal Data, and Data Subjects
                    </h2>
                  </div>
                  {openSections.schedule ? <ChevronUp className="h-5 w-5" data-id="element-907" data-mp="2lh51s89vm98g" /> : <ChevronDown className="h-5 w-5" data-id="element-908" data-mp="1v8xq8hwmm21a" />}
                </button>
                {openSections.schedule && <div className="p-6 bg-white" data-id="element-909" data-mp="24ax451ccgq00">
                    <p className="text-slate-dark mb-4" data-id="element-910" data-mp="24anfbh31covw">
                      This schedule provides further detail on the data
                      processing activities undertaken by Medicolegal
                      Psychiatry.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-911" data-mp="17lg34zob3s4n">
                      1. Processing by Medicolegal Psychiatry (as Data
                      Processor)
                    </h3>
                    <p className="text-slate-dark mb-4" data-id="element-912" data-mp="1gl2t8pbl5bie">
                      Purpose of Processing: To provide medico-legal services,
                      including assessments, expert opinion reports, and court
                      testimony, as instructed by you (the Data Controller).
                      This involves processing client personal data provided by
                      you.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-913" data-mp="34bsk1vjmgz3o">
                      Scope: Processing client personal data necessary to
                      conduct assessments, arrange visits, communicate with
                      clients, and produce expert reports.
                    </p>
                    <p className="text-slate-dark mb-4" data-id="element-914" data-mp="3j9b6uscqp28h">
                      Duration: For the duration of the contract and as long as
                      data retention is required by law or professional
                      guidelines.
                    </p>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-915" data-mp="3e96qr3fqu9is">
                      2. Types of Personal Data Processed (relating to Clients)
                    </h3>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-916" data-mp="1xwzwb4fryy1y">
                      <li className="mb-2" data-id="element-917" data-mp="22vl17h5z25wq">
                        Contact Details: Name, address, phone number, email.
                      </li>
                      <li className="mb-2" data-id="element-918" data-mp="1eqk3btueix0h">
                        Special Categories of Personal Data: Medical records,
                        mental health assessments, information about conditions,
                        reports on lifestyle, family history, legal case
                        details, and relevant historical data.
                      </li>
                      <li className="mb-2" data-id="element-919" data-mp="102p6c19d26hl">
                        Identification Data: Photographic identification, where
                        provided.
                      </li>
                      <li data-id="element-920" data-mp="r1980rvtlutq">
                        Assigned Reference Numbers: Unique identifiers assigned
                        to clients.
                      </li>
                    </ul>
                    <h3 className="text-lg font-bold text-primary mb-2" data-id="element-921" data-mp="2le8jc8t642v7">
                      3. Categories of Data Subjects
                    </h3>
                    <ul className="list-disc pl-6 text-slate-dark" data-id="element-922" data-mp="2nzga1i85u49b">
                      <li className="mb-2" data-id="element-923" data-mp="2nx2ihjcova6i">
                        The Subject: The individual undergoing assessment.
                      </li>
                      <li data-id="element-924" data-mp="2xogevi9cftvf">
                        The Instructing Party's Representatives: Case handlers,
                        solicitors, or other personnel from the instructing firm
                        who interact with us.
                      </li>
                    </ul>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}